import React, { useState, useCallback, useMemo } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import {
  Box,
  Button,
  Drawer,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {deleteOrgRole, getRoleAssignedUsers, getUserPermissionsList, getUserRolesList, upsertOrgRole } from '../../modules/config';
import { NoModificationIcon } from '../../ui/primitives/CustomIcons';
import TableComponent from '../../ui/data/table/TableComponent';
import ActionMenu from '../../ui/primitives/ActionMenu';
import RoleDrawerPanel from './RoleDrawerPanel';
import DeleteRoleModal from './DeleteRoleModal';

const normalizePermission = (p) => ({
  id: p.id,
  key: p.key || `${p.feature}:${p.action}`,
  name: p.name || p.summary,
  summary: p.summary || p.name,
  scope: p.scope,
  feature: p.feature,
  action: p.action,
  formattedName: p.name || `${p.action?.toUpperCase()} ${p.feature?.replaceAll('-', ' ')}`,
  description: p.description || '',
});

const RolesAndPermissions = ({ context = 'org' }) => {
  const isSuperAdmin = context === 'super-admin';

  const [activeTab, setActiveTab] = useState(0);
  const [panel, setPanel] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchText, setSearchText] = useState('');
  const [permSearch, setPermSearch] = useState('');
  const [permPage, setPermPage] = useState(1);
  const [permPerPage, setPermPerPage] = useState(10);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [assignedData, setAssignedData] = useState(null);
  const [selectedNewRoleId, setSelectedNewRoleId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [saveError, setSaveError] = useState('');

  const scope = useMemo(() => {
    if (isSuperAdmin) return null;
    return activeTab === 0 ? 'org' : 'project';
  }, [activeTab, isSuperAdmin]);

  const { data: rolesResponse, isLoading: rolesLoading, refetch: refetchRoles } = useQuery({
    queryKey: ['roles', context],
    queryFn: () => getUserRolesList({ includePermissions: true, superAdminMode: isSuperAdmin }),
  });

  const { data: permissionsResponse, isLoading: permissionsLoading, refetch: refetchPermissions } = useQuery({
    queryKey: ['permissions', permSearch, permPage, permPerPage, Boolean(panel)],
    queryFn: () => getUserPermissionsList(
      isSuperAdmin && !panel
        ? { search: permSearch || undefined, page: permPage, perPage: permPerPage }
        : {}
    ),
  });

  const permissionsData = useMemo(() => permissionsResponse?.data || [], [permissionsResponse]);
  const permissionsTotal = useMemo(() => permissionsResponse?.total || 0, [permissionsResponse]);

  const roles = useMemo(() =>
    (rolesResponse || []).map((r) => ({
      id: r.id,
      name: r.name,
      scope: r.scope,
      key: r.key || '',                                      
      isSystemRole: !!r.is_system_role  ,
      organizationId: r.organization_id,   
      perms: r.permission_ids ?? [],        
      desc: r.description || '',
    })),
    [rolesResponse]
  );

  const permissions = useMemo(() => permissionsData.map(normalizePermission), [permissionsData]);



  const unifiedRolesList = useMemo(() => {
    if (isSuperAdmin) return roles.filter((r) => r.isSystemRole);
    return roles.filter((r) => r.scope === scope);
  }, [roles, scope, isSuperAdmin]);

  const reassignableRoles = useMemo(() => {
    if (!roleToDelete) return [];
    return roles.filter((r) => {
      if (r.id === roleToDelete.id) return false;
      if (isSuperAdmin) return r.isSystemRole;
      if (roleToDelete.scope === 'org') return r.scope === 'org' && r.key !== 'org-admin' && r.key !== 'super-admin' && r.name !== 'Organization Admin' && r.name !== 'Super Admin';
      return r.scope === 'project';
    });
  }, [roles, roleToDelete, isSuperAdmin]);

  const openPanel = useCallback((mode, role = null) => {
    setSaveError('');
    setPanel({ mode, role });
  }, []);

  const closePanel = useCallback(() => setPanel(null), []);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSearchText('');
    setPermSearch('');
    setPermPage(1);
    closePanel();
  };

  const handleSave = async ({ name, desc, perms, roleScope }) => {
    setSaveError('');
    try {
      if (panel.mode === 'create') {
        await upsertOrgRole({ name, desc, perms, scope: isSuperAdmin ? roleScope : scope });
        await refetchRoles();
        closePanel();
      } else if (panel.mode === 'edit') {
        await upsertOrgRole({ roleId: panel.role.id, name, desc, perms, scope: isSuperAdmin ? roleScope : panel.role.scope });
        await refetchRoles();
        closePanel();
      }
    } catch (err) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      setSaveError(msg);
      throw err;
    }
  };

  // Opens the delete modal and simultaneously fetches assigned users
  const handleDeleteTrigger = useCallback((role) => {
    if (role.isSystemRole) return;

    setRoleToDelete(role);
    setAssignedData(null);
    setSelectedNewRoleId('');
    setDeleteError('');
    setDeleteModalOpen(true);
    setUsersLoading(true);

    getRoleAssignedUsers(role.id)
      .then((data) => setAssignedData(data))
      .catch((err) => setDeleteError(err?.message || 'Failed to load role assignments.'))
      .finally(() => setUsersLoading(false));
  }, []);

  const handleNewRoleChange = useCallback((newRoleId) => {
    setSelectedNewRoleId(newRoleId);
  }, []);

  const handleConfirmDelete = async () => {
    if (!roleToDelete || roleToDelete.isSystemRole) return;

    const hasAssignees =
      assignedData &&
      ((assignedData.scope === 'org' && assignedData.users?.length > 0) ||
        (assignedData.scope === 'project' && assignedData.assignments?.length > 0));

    if (hasAssignees && !selectedNewRoleId) {
      setDeleteError('Please select a replacement role before proceeding.');
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      let reassignments = [];
      if (hasAssignees) {
        if (assignedData.scope === 'org') {
          reassignments = assignedData.users.map((u) => ({
            userId: u.id ?? u.user_id,
            newRoleId: selectedNewRoleId,
          }));
        } else {
          reassignments = assignedData.assignments.flatMap((a) =>
            (a.assignments || []).map((sub) => ({
              assignmentId: sub.assignment_id ?? sub.assignmentId,
              newRoleId: selectedNewRoleId,
            }))
          );
        }
      }

      await deleteOrgRole({ roleId: roleToDelete.id, reassignments });
      await refetchRoles();
      setDeleteModalOpen(false);
      setRoleToDelete(null);
      setAssignedData(null);
      setSelectedNewRoleId('');
      closePanel();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete role. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteModalClose = useCallback(() => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteError('');
  }, [deleteLoading]);


  const tableHeaders = useMemo(() => {
    if (isSuperAdmin && activeTab === 1) {
      return [
        { key: 'summary', label: 'Permission Name' },
        { key: 'key', label: 'System Key String' },
        { key: 'description', label: 'Description' },
        { key: 'scope', label: 'Scope' },
        { key: 'actions', label: '' },
      ];
    }
    return [
      { key: 'name', label: 'Role Name' },
      { key: 'desc', label: 'Description' },
      { key: 'permissions', label: 'Assigned Permissions' },
      { key: 'actions', label: '' },
    ];
  }, [isSuperAdmin, activeTab]);

  const tableData = useMemo(() => {
    if (isSuperAdmin && activeTab === 1) {
      return permissions.map((p) => ({
        id: p.id,
        summary: p.summary,
        key: p.key,
        scope: p.scope ? p.scope.toUpperCase() : '—',
        description: p.description,
        actions: (
          <ActionMenu
            tooltip="Permission options"
            items={[
              {
                label: 'Edit Permission',
                icon: <EditOutlinedIcon />,
                onClick: () => openPanel('edit-perm', p),
              },
            ]}
          />
        ),
      }));
    }

    return unifiedRolesList.map((role) => {
      const isEditable = !role.isSystemRole;
      return {
        id: role.id,
        name: (
          <Typography
            onClick={() => openPanel(role.isSystemRole ? 'view-sys' : 'view-custom', role)}
            variant="body2"
            sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {role.name}
          </Typography>
        ),
        desc: role.desc || '—',
        permissions: role.perms.length,
        actions: !isEditable ? (
          <Box sx={{ display: 'inline-flex', color: 'text.disabled', pr: 1 }} title="System roles cannot be modified">
            <NoModificationIcon sx={{ fontSize: 18 }} />
          </Box>
        ) : (
          <ActionMenu
            tooltip="Role options"
            items={[
              {
                label: 'Edit Role',
                icon: <EditOutlinedIcon />,
                onClick: () => openPanel('edit', role),
              },
              {
                label: 'Delete Role',
                icon: <DeleteOutlineIcon />,
                color: 'error',
                onClick: () => handleDeleteTrigger(role),
              },
            ]}
          />
        ),
      };
    });
  }, [unifiedRolesList, permissions, isSuperAdmin, activeTab, openPanel, handleDeleteTrigger]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon color="primary" />
            {isSuperAdmin ? 'System Access Control' : 'Organization Access Control'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSuperAdmin
              ? 'Configure global system roles, map cross-organization parameters, and manage permission nodes.'
              : 'Manage roles, set up permissions, and control user access across your organization.'}
          </Typography>
        </Box>
        {isSuperAdmin ? (
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openPanel(activeTab === 0 ? 'create' : 'create-perm')}
            sx={{ height: 40 }}
          >
            Create {activeTab === 0 ? 'System Role' : 'Permission'}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openPanel('create')}
            sx={{ height: 40 }}
          >
            Create Custom Role
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ borderBottom: 1, borderColor: 'divider', background: 'transparent', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label={isSuperAdmin ? 'System Roles' : 'Organization Roles'} />
          <Tab label={isSuperAdmin ? 'Permissions Directory' : 'Project Roles'} />
        </Tabs>
      </Paper>

      <TableComponent
        data={tableData}
        headerTabs={tableHeaders}
        showCheckbox={false}
        defaultSort={isSuperAdmin && activeTab === 1 ? 'summary' : 'name'}
        order={sortOrder}
        setOrder={setSortOrder}
        tablekey={isSuperAdmin && activeTab === 1 ? 'permissions' : 'roles'}
        isLoading={rolesLoading || permissionsLoading}
        searchValue={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        searchPlaceholder={activeTab === 0 ? 'Search configurations...' : 'Search master keys...'}
        striped={isSuperAdmin}
        {...(isSuperAdmin && activeTab === 1 && {
          showSearch: true,
          onSearch: (val) => { setPermSearch(val); setPermPage(1); },
          searchPlaceholder: 'Search by name or description…',
          showPages: true,
          page: permPage,
          perPage: permPerPage,
          totalCount: permissionsTotal,
          handlePageChange: (p) => setPermPage(p),
          showCount: true,
          showRowCount: true,
          setPerPage: (e) => { setPermPerPage(e.target.value); setPermPage(1); },
          serverSideSorting: true,
        })}
      />

      <Drawer
        anchor="right"
        open={Boolean(panel)}
        onClose={closePanel}
        PaperProps={{ className: 'app-right-drawer' }}
      >
        <RoleDrawerPanel
          panel={panel}
          scope={scope}
          permissions={permissions}
          onClose={closePanel}
          onEdit={() => openPanel('edit', panel?.role)}
          onDelete={() => handleDeleteTrigger(panel?.role)}
          onSave={handleSave}
          refetchPermissions={refetchPermissions}
          isSuperAdmin={isSuperAdmin}
          apiError={saveError}
          onClearError={() => setSaveError('')}
        />
      </Drawer>

      <DeleteRoleModal
        open={deleteModalOpen}
        loading={deleteLoading}
        usersLoading={usersLoading}
        roleToDelete={roleToDelete}
        assignedData={assignedData}
        selectedNewRoleId={selectedNewRoleId}
        onNewRoleChange={handleNewRoleChange}
        reassignableRoles={reassignableRoles}
        onClose={handleDeleteModalClose}
        onConfirm={handleConfirmDelete}
        error={deleteError}
        onClearError={() => setDeleteError('')}
      />
    </Box>
  );
};

export default RolesAndPermissions;